import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DateRange } from 'react-day-picker'

import { Region } from '@/types'
import { getBranchesByTenant, getDefaultBranchType } from '@/utils'
import { DateType } from '@/utils/date'
import { RootState } from './store'
import { detectTenant } from './tenantSlice'

/**
 * Branch selection type
 * 'all': All stores selected
 * 'same': Same stores selected (existing stores)
 * 'new': New stores selected
 * 'regular': Branch selection outside of all, same, or new
 */
export type BranchType = 'all' | 'same' | 'new' | 'regular'

// ============================================================================
// DATE CONTEXT TYPES (Foundation)
// ============================================================================

/**
 * Date context type - determines which date defaults and format to use.
 * - 'monthly': For pages like Sales, Labor, SoS, Loyalty (uses yyyy-mm-dd format)
 * - 'quarterly': For pages like Spend, PnL (uses yyyy-mm format)
 */
export type DateContextType = 'monthly' | 'quarterly'

/**
 * State for a single date context.
 * Each context maintains its own date range and filter type.
 */
export interface DateContextState {
  date: DateRange | undefined
  dateFilterType: DateType
}

export type CountryFilterType = 'US' | 'UK' | undefined

/**
 * Mapping of page IDs to their date context type.
 * This determines which date defaults to use when navigating to a page.
 */
export const PAGE_DATE_CONTEXT: Record<string, DateContextType> = {
  // Monthly context pages (use month-to-date, yyyy-mm-dd format)
  sales: 'monthly',
  labor: 'monthly',
  'speed-of-service': 'monthly',
  loyalty: 'monthly',
  dashboard: 'monthly',
  'sales-lens': 'monthly',
  'sales-lens-item': 'monthly',

  // Quarterly context pages (use quarterly, yyyy-mm format)
  spend: 'quarterly',
  'profit-and-loss': 'quarterly',
  'spend-lens': 'quarterly',
  'spend-lens-item': 'quarterly',
  'pnl-lens': 'quarterly',
  'labor-lens': 'quarterly',
}

/**
 * Get the date context type for a given page ID.
 * Defaults to 'monthly' for unknown pages.
 */
export const getDateContextForPage = (pageId: string): DateContextType => {
  return PAGE_DATE_CONTEXT[pageId] || 'monthly'
}

/**
 * UNIFIED FILTER STATE
 *
 * This single state replaces 3 separate contexts (Base, Comparison, SalesLens).
 *
 * Core filters (used by all pages):
 * selectedBranch, selectedBranchType, date, dateFilterType
 *
 * Optional filters (used by specific page types):
 * comparisonBranch: For lens pages that need comparison (PnL Lens)
 * temperatureFilter, sizeFilter: For sales-lens pages (Sales Lens Items)
 * typeFilter, chartType: For lens pages with charts
 */
export interface FilterState {
  // Core filters - used by all pages
  selectedBranch: string
  selectedBranchType: BranchType

  // ===== Separated Date Contexts =====
  /**
   * Date state separated by context type.
   * Each context maintains its own date range and filter type.
   * This prevents stale state when navigating between monthly/quarterly pages.
   */
  dateContexts: {
    monthly: DateContextState
    quarterly: DateContextState
  }

  /**
   * The currently active date context.
   * Determines which context's date values are used by selectors.
   */
  activeDateContext: DateContextType

  /**
   * Tracks which date contexts have been initialized.
   * Prevents duplicate initialization on navigation.
   */
  dateContextInitialized: {
    monthly: boolean
    quarterly: boolean
  }

  // Comparison filter(PnL Lens)
  comparisonBranch?: string

  // Sales Lens specific filters
  temperatureFilter?: string
  sizeFilter?: string

  // PnL specific filters
  countryFilter?: CountryFilterType

  // PnL-specific branch state (used when country != default)
  // null = use global selectedBranch; non-null = PnL has its own branch selection
  pnlBranch: string | null
  pnlBranchType: BranchType

  // Metric filters (for lens pages with charts)
  typeFilter?: string
  chartType?: string

  // Page specific defaults for branch filters (tenant-aware)
  defaults: {
    defaultBranch: string
    defaultBranchType: BranchType
  }

  // Refresh callback registry
  refreshCallbacks: Record<string, boolean>
}

/**
 * INITIAL STATE
 *
 * Default values when the app first loads.
 * selectedBranch and selectedBranchType are initialized using tenant detection.
 * - blankstreet: 'all' stores by default
 * - dev: 'same' stores by default
 */
const initialTenant = detectTenant()
// Get from sessionStorage to avoid circular dependency
const initialDefaultBranchValue = getBranchesByTenant(initialTenant)
const initialDefaultBranchType: BranchType = getDefaultBranchType(initialTenant)

// Default dates for each context (tenant-aware)
const getInitialMonthlyDate = (): DateRange => ({
  from: new Date(2024, 6, 1), // July 1, 2024
  to: new Date(2024, 6, 26), // July 26, 2024
})

const getInitialQuarterlyDate = (): DateRange => {
  if (initialTenant === 'blankstreet') {
    // Default: 2025 Q3 (July 1 – September 30, 2025)
    return {
      from: new Date(2025, 6, 1), // July 1, 2025
      to: new Date(2025, 8, 30), // September 30, 2025
    }
  }
  // dev tenant - Q1 2024
  return {
    from: new Date(2024, 0, 1), // January 1, 2024 (Q1)
    to: new Date(2024, 2, 31), // March 31, 2024
  }
}

const initialState: FilterState = {
  selectedBranch: initialDefaultBranchValue, // Joined branch names from sessionStorage
  selectedBranchType: initialDefaultBranchType,

  // Separated date contexts
  dateContexts: {
    monthly: {
      date: getInitialMonthlyDate(),
      dateFilterType: 'month-to-date',
    },
    quarterly: {
      date: getInitialQuarterlyDate(),
      dateFilterType: 'quarterly',
    },
  },
  activeDateContext: 'monthly',
  dateContextInitialized: {
    monthly: false,
    quarterly: false,
  },

  // Other filters
  comparisonBranch: '',
  temperatureFilter: undefined,
  sizeFilter: undefined,
  countryFilter: 'UK',
  pnlBranch: null,
  pnlBranchType: 'all',
  typeFilter: undefined,
  chartType: undefined,
  defaults: {
    defaultBranch: initialDefaultBranchValue,
    defaultBranchType: initialDefaultBranchType,
  },
  refreshCallbacks: {},
}

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setSelectedBranch: (
      state,
      action: PayloadAction<{ branch: string; branchType: BranchType }>
    ) => {
      state.selectedBranch = action.payload.branch
      state.selectedBranchType = action.payload.branchType
    },

    setSelectedBranchType: (state, action: PayloadAction<BranchType>) => {
      state.selectedBranchType = action.payload
    },

    /**
     * Comparison filter setter
     */
    setComparisonBranch: (state, action: PayloadAction<string>) => {
      state.comparisonBranch = action.payload
    },

    /**
     * Sales-lens filter setters
     */
    setTemperatureFilter: (state, action: PayloadAction<string>) => {
      state.temperatureFilter = action.payload
    },

    setSizeFilter: (state, action: PayloadAction<string>) => {
      state.sizeFilter = action.payload
    },

    /**
     * PnL specific filter setters
     */
    setCountryFilter: (state, action: PayloadAction<CountryFilterType>) => {
      state.countryFilter = action.payload
    },

    /**
     * Set PnL-specific branch selection.
     * Used when PnL page has a non-default country (e.g. GBP)
     * so that PnL branches are stored separately from the global selection.
     */
    setPnlBranch: (
      state,
      action: PayloadAction<{ branch: string; branchType: BranchType }>
    ) => {
      state.pnlBranch = action.payload.branch
      state.pnlBranchType = action.payload.branchType
    },

    /**
     * Clear PnL-specific branch selection.
     * PnL page will fall back to global selectedBranch.
     */
    clearPnlBranch: (state) => {
      state.pnlBranch = null
      state.pnlBranchType = 'all'
    },

    /**
     * Metric filter setters
     */
    setTypeFilter: (state, action: PayloadAction<string>) => {
      state.typeFilter = action.payload
    },

    setChartType: (state, action: PayloadAction<string>) => {
      state.chartType = action.payload
    },

    /**
     * Reset filters to their default values
     * Uses explicit defaults matching initialState for consistency
     * Also resets date contexts to their initial defaults
     */
    resetFilters: (state) => {
      state.selectedBranch = state.defaults.defaultBranch
      state.selectedBranchType = state.defaults.defaultBranchType
      state.comparisonBranch = ''
      state.temperatureFilter = undefined
      state.sizeFilter = undefined
      state.countryFilter = 'UK'
      state.pnlBranch = null
      state.pnlBranchType = 'all'
      state.typeFilter = undefined
      state.chartType = undefined

      state.dateContexts = {
        monthly: {
          date: getInitialMonthlyDate(),
          dateFilterType: 'month-to-date',
        },
        quarterly: {
          date: getInitialQuarterlyDate(),
          dateFilterType: 'quarterly',
        },
      }

      state.dateContextInitialized = {
        monthly: true,
        quarterly: true,
      }
    },

    /**
     * Trigger refresh for a specific page
     * Components will watch this flag to know when to refetch data
     */
    triggerRefresh: (state, action: PayloadAction<string>) => {
      const pageId = action.payload
      state.refreshCallbacks[pageId] = !state.refreshCallbacks[pageId]
    },

    // =========================================================================
    // Date Context Management Reducers
    // These prepare for Phase 2 but are usable now for gradual migration.
    // =========================================================================

    /**
     * Set the active date context and sync legacy fields.
     * Called when navigating between pages with different date requirements.
     * This ensures backwards compatibility by keeping legacy `date` and
     * `dateFilterType` fields in sync with the active context.
     */
    setActiveDateContext: (state, action: PayloadAction<DateContextType>) => {
      state.activeDateContext = action.payload
    },

    /**
     * Mark a date context as initialized.
     * Prevents duplicate initialization when navigating back to a page.
     */
    setDateContextInitialized: (
      state,
      action: PayloadAction<{ context: DateContextType; initialized: boolean }>
    ) => {
      const { context, initialized } = action.payload
      state.dateContextInitialized[context] = initialized
    },

    /**
     * Reset date context initialization flags.
     * Useful for testing or when forcing re-initialization.
     */
    resetDateContextInitialization: (state) => {
      state.dateContextInitialized = {
        monthly: false,
        quarterly: false,
      }
    },

    // =========================================================================
    // Date Context Reducers
    // These manage the separated date contexts.
    // =========================================================================

    /**
     * Set date for a specific context.
     */
    setDateForContext: (
      state,
      action: PayloadAction<{
        context: DateContextType
        date: DateRange | undefined
      }>
    ) => {
      const { context, date } = action.payload
      state.dateContexts[context].date = date
    },

    /**
     * Set date filter type for a specific context.
     */
    setDateFilterTypeForContext: (
      state,
      action: PayloadAction<{
        context: DateContextType
        dateFilterType: DateType
      }>
    ) => {
      const { context, dateFilterType } = action.payload
      state.dateContexts[context].dateFilterType = dateFilterType
    },

    /**
     * Initialize a date context with default values.
     * IDEMPOTENT: Only initializes if the context hasn't been initialized yet.
     * This allows the action to be dispatched freely without conditional logic in hooks.
     */
    initializeDateContext: (
      state,
      action: PayloadAction<{
        context: DateContextType
        date: DateRange
        dateFilterType: DateType
      }>
    ) => {
      const { context, date, dateFilterType } = action.payload

      // Idempotent check - skip if already initialized
      if (state.dateContextInitialized[context]) {
        return
      }

      // Initialize the context
      state.dateContexts[context] = { date, dateFilterType }
      state.dateContextInitialized[context] = true
    },

    /**
     * Reset a specific date context to its default values.
     */
    resetDateContext: (
      state,
      action: PayloadAction<{
        context: DateContextType
        defaultDate: DateRange
        defaultDateFilterType: DateType
      }>
    ) => {
      const { context, defaultDate, defaultDateFilterType } = action.payload

      state.dateContexts[context] = {
        date: defaultDate,
        dateFilterType: defaultDateFilterType,
      }
    },

    /**
     * Sync branches when regions are loaded from API.
     * Called explicitly after login/refresh regions to update branch selection
     * when branchType is 'all' but initial state had stale/fallback values.
     */
    syncBranchesFromRegions: (state, action: PayloadAction<Region[]>) => {
      if (state.selectedBranchType === 'all') {
        const filteredRegions = action.payload.filter(
          (region) => region.name !== 'Other'
        )
        const allBranches = filteredRegions.flatMap((region) =>
          region.branches.map((branch) => branch.name)
        )
        const joinedBranches = allBranches.join(',')

        state.selectedBranch = joinedBranches

        state.defaults.defaultBranch = joinedBranches
      }
    },
  },
})

export const {
  setSelectedBranch,
  setSelectedBranchType,
  setComparisonBranch,
  setTemperatureFilter,
  setSizeFilter,
  setCountryFilter,
  setPnlBranch,
  clearPnlBranch,
  setTypeFilter,
  setChartType,
  resetFilters,
  triggerRefresh,
  // Date context management
  setActiveDateContext,
  setDateContextInitialized,
  resetDateContextInitialization,
  // Context-aware date management
  setDateForContext,
  setDateFilterTypeForContext,
  initializeDateContext,
  resetDateContext,
  syncBranchesFromRegions,
} = filterSlice.actions

export default filterSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

// Core filters
export const selectSelectedBranch = (state: RootState) =>
  state.filters.selectedBranch
export const selectSelectedBranchType = (state: RootState) =>
  state.filters.selectedBranchType

// Comparison filters
export const selectComparisonBranch = (state: RootState) =>
  state.filters.comparisonBranch

// Sales Lens filters
export const selectTemperatureFilter = (state: RootState) =>
  state.filters.temperatureFilter
export const selectSizeFilter = (state: RootState) => state.filters.sizeFilter

// PnL filters
export const selectCountryFilter = (state: RootState) =>
  state.filters.countryFilter
export const selectPnlBranch = (state: RootState) => state.filters.pnlBranch
export const selectPnlBranchType = (state: RootState) =>
  state.filters.pnlBranchType

// Metric filters
export const selectTypeFilter = (state: RootState) => state.filters.typeFilter
export const selectChartType = (state: RootState) => state.filters.chartType

// System state
export const selectRefreshCallbacks = (state: RootState) =>
  state.filters.refreshCallbacks
export const selectPageRefreshTrigger =
  (pageId: string) => (state: RootState) =>
    state.filters.refreshCallbacks[pageId]

// Generic selector for whole state (use carefully)
export const selectFilterState = (state: RootState) => state.filters

// ============================================================================
// Date Context Selectors
// ============================================================================

/**
 * Select the currently active date context type.
 */
export const selectActiveDateContext = (state: RootState) =>
  state.filters.activeDateContext

/**
 * Select the initialization state for all contexts.
 */
export const selectDateContextInitialized = (state: RootState) =>
  state.filters.dateContextInitialized

/**
 * Select whether a specific context has been initialized.
 */
export const selectIsContextInitialized =
  (context: DateContextType) => (state: RootState) =>
    state.filters.dateContextInitialized[context]

/**
 * Select whether the currently active context has been initialized.
 * Useful for guarding data fetching until filters are ready.
 */
export const selectIsActiveContextInitialized = (state: RootState) =>
  state.filters.dateContextInitialized[state.filters.activeDateContext]

// ============================================================================
// Context-Aware Date Selectors
// ============================================================================

/**
 * Select the entire dateContexts object.
 */
export const selectDateContexts = (state: RootState) =>
  state.filters.dateContexts

/**
 * Select date from the currently active context.
 * This is the recommended selector for getting the current date.
 */
export const selectActiveDate = (state: RootState) =>
  state.filters.dateContexts[state.filters.activeDateContext].date

/**
 * Select date filter type from the currently active context.
 * This is the recommended selector for getting the current date filter type.
 */
export const selectActiveDateFilterType = (state: RootState) =>
  state.filters.dateContexts[state.filters.activeDateContext].dateFilterType

/**
 * Factory selector to get date for a specific context.
 * Usage: useAppSelector(selectDateByContext('monthly'))
 */
export const selectDateByContext =
  (context: DateContextType) => (state: RootState) =>
    state.filters.dateContexts[context].date

/**
 * Factory selector to get date filter type for a specific context.
 * Usage: useAppSelector(selectDateFilterTypeByContext('quarterly'))
 */
export const selectDateFilterTypeByContext =
  (context: DateContextType) => (state: RootState) =>
    state.filters.dateContexts[context].dateFilterType

/**
 * Select the complete state for the active date context.
 * Returns { date, dateFilterType } for the active context.
 */
export const selectActiveDateContextState = (state: RootState) =>
  state.filters.dateContexts[state.filters.activeDateContext]
